import { useState } from "react";
import { useAuthStore } from "../stores/authStore";
import { adminApi } from "../services/api";
import { useI18n } from "@expertloop/i18n/react";
import "./SecuritySettingsPage.css";

interface TwoFactorSetupData {
	secret: string;
	qrCodeDataUrl: string;
	recoveryCodes: string[];
}

export function SecuritySettingsPage() {
	const { t } = useI18n();
	const admin = useAuthStore((s) => s.admin);
	const fetchAdmin = useAuthStore((s) => s.fetchAdmin);
	const updateToken = useAuthStore((s) => s.updateToken);

	// Password change state
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [passwordSuccess, setPasswordSuccess] = useState("");
	const [passwordLoading, setPasswordLoading] = useState(false);

	// 2FA state
	const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
	const [verifyCode, setVerifyCode] = useState("");
	const [twoFAError, setTwoFAError] = useState("");
	const [twoFALoading, setTwoFALoading] = useState(false);
	const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
	const [disablePassword, setDisablePassword] = useState("");
	const [showDisableDialog, setShowDisableDialog] = useState(false);

	// Session invalidation state
	const [sessionPassword, setSessionPassword] = useState("");
	const [sessionError, setSessionError] = useState("");
	const [sessionSuccess, setSessionSuccess] = useState("");
	const [sessionLoading, setSessionLoading] = useState(false);

	const handlePasswordChange = async (e: React.FormEvent) => {
		e.preventDefault();
		setPasswordError("");
		setPasswordSuccess("");

		if (newPassword !== confirmPassword) {
			setPasswordError(t("auth.register.passwordMismatch"));
			return;
		}

		if (newPassword.length < 8) {
			setPasswordError(t("auth.register.passwordTooShort"));
			return;
		}

		setPasswordLoading(true);
		try {
			const response = await adminApi.post<{ token: string }>("/admin/auth/change-password", {
				currentPassword,
				newPassword,
			});
			updateToken(response.token);
			setPasswordSuccess(t("adminSecurity.passwordChanged"));
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
		} catch (err) {
			setPasswordError(err instanceof Error ? err.message : t("adminSecurity.passwordChangeFailed"));
		} finally {
			setPasswordLoading(false);
		}
	};

	const handleSetup2FA = async () => {
		setTwoFAError("");
		setTwoFALoading(true);
		try {
			const data = await adminApi.post<TwoFactorSetupData>("/admin/auth/2fa/setup", {});
			setSetupData(data);
		} catch (err) {
			setTwoFAError(err instanceof Error ? err.message : t("adminSecurity.setupFailed"));
		} finally {
			setTwoFALoading(false);
		}
	};

	const handleVerify2FA = async (e: React.FormEvent) => {
		e.preventDefault();
		setTwoFAError("");
		setTwoFALoading(true);
		try {
			await adminApi.post("/admin/auth/2fa/verify-setup", { code: verifyCode });
			setShowRecoveryCodes(true);
		} catch (err) {
			setTwoFAError(err instanceof Error ? err.message : t("adminSecurity.invalidCode"));
		} finally {
			setTwoFALoading(false);
		}
	};

	const handleComplete2FA = async () => {
		setSetupData(null);
		setVerifyCode("");
		setShowRecoveryCodes(false);
		await fetchAdmin();
	};

	const handleDisable2FA = async () => {
		setTwoFAError("");
		setTwoFALoading(true);
		try {
			await adminApi.post("/admin/auth/2fa/disable", { password: disablePassword });
			setShowDisableDialog(false);
			setDisablePassword("");
			await fetchAdmin();
		} catch (err) {
			setTwoFAError(err instanceof Error ? err.message : t("adminSecurity.disableFailed"));
		} finally {
			setTwoFALoading(false);
		}
	};

	const handleInvalidateSessions = async (e: React.FormEvent) => {
		e.preventDefault();
		setSessionError("");
		setSessionSuccess("");
		setSessionLoading(true);
		try {
			const response = await adminApi.post<{ token: string }>("/admin/auth/invalidate-sessions", {
				password: sessionPassword,
			});
			updateToken(response.token);
			setSessionSuccess(t("adminSecurity.sessionsInvalidated"));
			setSessionPassword("");
		} catch (err) {
			setSessionError(err instanceof Error ? err.message : t("adminSecurity.invalidateFailed"));
		} finally {
			setSessionLoading(false);
		}
	};

	return (
		<div className="security-settings-page">
			<h1>{t('adminNav.securitySettings')}</h1>

			{/* Password Change Section */}
			<section className="settings-section">
				<h2>{t('adminSecurity.changePassword')}</h2>
				<form onSubmit={handlePasswordChange} className="settings-form">
					<div className="form-group">
						<label htmlFor="currentPassword">{t('adminSecurity.currentPassword')}</label>
						<input
							type="password"
							id="currentPassword"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="newPassword">{t('adminSecurity.newPassword')}</label>
						<input
							type="password"
							id="newPassword"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							required
							minLength={8}
						/>
					</div>
					<div className="form-group">
						<label htmlFor="confirmPassword">{t('adminSecurity.confirmNewPassword')}</label>
						<input
							type="password"
							id="confirmPassword"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							required
						/>
					</div>
					{passwordError && <div className="error-message">{passwordError}</div>}
					{passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
					<button type="submit" className="btn btn-primary" disabled={passwordLoading}>
						{passwordLoading ? t('adminSecurity.changing') : t('adminSecurity.changePassword')}
					</button>
				</form>
			</section>

			{/* Two-Factor Authentication Section */}
			<section className="settings-section">
				<h2>{t('adminLogin.twoFactorTitle')}</h2>
				<p className="section-description">{t('adminSecurity.twoFaDescription')}</p>

				{admin?.twoFactorEnabled ? (
					<div className="two-fa-enabled">
						<div className="status-badge enabled">{t('adminSecurity.twoFaEnabled')}</div>
						<button
							className="btn btn-danger"
							onClick={() => setShowDisableDialog(true)}
						>
							{t('adminSecurity.disable2fa')}
						</button>
					</div>
				) : setupData ? (
					<div className="two-fa-setup">
						{showRecoveryCodes ? (
							<div className="recovery-codes">
								<h3>{t('adminSecurity.saveRecoveryCodes')}</h3>
								<p>{t('adminSecurity.saveRecoveryCodesBody')}</p>
								<div className="codes-grid">
									{setupData.recoveryCodes.map((code, i) => (
										<code key={i}>{code}</code>
									))}
								</div>
								<button className="btn btn-primary" onClick={handleComplete2FA}>
									{t('adminSecurity.savedCodes')}
								</button>
							</div>
						) : (
							<>
								<div className="qr-code-container">
									<img src={setupData.qrCodeDataUrl} alt={t('adminSecurity.qrCodeAlt')} />
									<p>{t('adminSecurity.scanQrCode')}</p>
								</div>
								<div className="manual-entry">
									<p>{t('adminSecurity.enterManually')}</p>
									<code className="secret-code">{setupData.secret}</code>
								</div>
								<form onSubmit={handleVerify2FA} className="verify-form">
									<div className="form-group">
										<label htmlFor="verifyCode">{t('adminSecurity.enterCodeFromApp')}</label>
										<input
											type="text"
											id="verifyCode"
											value={verifyCode}
											onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
											placeholder="000000"
											maxLength={6}
											required
										/>
									</div>
									{twoFAError && <div className="error-message">{twoFAError}</div>}
									<div className="form-actions">
										<button type="submit" className="btn btn-primary" disabled={twoFALoading || verifyCode.length !== 6}>
											{twoFALoading ? t('adminLogin.verifying') : t('adminSecurity.verifyAndEnable')}
										</button>
										<button
											type="button"
											className="btn btn-secondary"
											onClick={() => setSetupData(null)}
										>
											{t('common.cancel')}
										</button>
									</div>
								</form>
							</>
						)}
					</div>
				) : (
					<>
						{twoFAError && <div className="error-message">{twoFAError}</div>}
						<button
							className="btn btn-primary"
							onClick={handleSetup2FA}
							disabled={twoFALoading}
						>
							{twoFALoading ? t('adminSecurity.settingUp') : t('adminSecurity.setup2fa')}
						</button>
					</>
				)}
			</section>

			{/* Session Management Section */}
			<section className="settings-section">
				<h2>{t('adminSecurity.sessionManagement')}</h2>
				<p className="section-description">{t('adminSecurity.sessionManagementBody')}</p>
				<form onSubmit={handleInvalidateSessions} className="settings-form">
					<div className="form-group">
						<label htmlFor="sessionPassword">{t('adminSecurity.confirmWithPassword')}</label>
						<input
							type="password"
							id="sessionPassword"
							value={sessionPassword}
							onChange={(e) => setSessionPassword(e.target.value)}
							required
						/>
					</div>
					{sessionError && <div className="error-message">{sessionError}</div>}
					{sessionSuccess && <div className="success-message">{sessionSuccess}</div>}
					<button type="submit" className="btn btn-warning" disabled={sessionLoading}>
						{sessionLoading ? t('adminSecurity.invalidating') : t('adminSecurity.invalidateAllSessions')}
					</button>
				</form>
			</section>

			{/* Disable 2FA Dialog */}
			{showDisableDialog && (
				<div className="dialog-overlay">
					<div className="dialog">
						<h3>{t('adminSecurity.disableTwoFaTitle')}</h3>
						<p>{t('adminSecurity.disableTwoFaBody')}</p>
						<div className="form-group">
							<input
								type="password"
								value={disablePassword}
								onChange={(e) => setDisablePassword(e.target.value)}
								placeholder={t('auth.login.password')}
							/>
						</div>
						{twoFAError && <div className="error-message">{twoFAError}</div>}
						<div className="dialog-actions">
							<button
								className="btn btn-danger"
								onClick={handleDisable2FA}
								disabled={twoFALoading || !disablePassword}
							>
								{twoFALoading ? t('adminSecurity.disabling') : t('adminSecurity.disable2fa')}
							</button>
							<button
								className="btn btn-secondary"
								onClick={() => {
									setShowDisableDialog(false);
									setDisablePassword("");
									setTwoFAError("");
								}}
							>
								{t('common.cancel')}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
